import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input
} from '@smorgrav/velpr-ui'
import { Loader2, Search, UserPen } from 'lucide-react'
import { useUserAdmin, type AdminUserView } from '@/hooks/useUserAdmin'

export const Route = createFileRoute('/users')({
  component: UsersPage
})

const isEmail = (s: string) => /.+@.+\..+/.test(s.trim())

function UsersPage() {
  const { lookupUser, changeEmail, loading } = useUserAdmin()

  const [query, setQuery] = useState('')
  const [found, setFound] = useState<AdminUserView | null>(null)
  const [newEmail, setNewEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const onSearch = async () => {
    setError(null)
    setNotice(null)
    setFound(null)
    setNewEmail('')
    try {
      setFound(await lookupUser(query.trim()))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const onChangeEmail = async () => {
    if (!found) return
    const target = newEmail.trim()
    setError(null)
    setNotice(null)
    if (!isEmail(target)) {
      setError('Skriv inn en gyldig ny e-postadresse.')
      return
    }
    if (target === found.email) {
      setError('Ny e-postadresse er lik den nåværende.')
      return
    }
    if (
      !window.confirm(
        `Endre e-post for ${found.email ?? found.uid} til ${target}?\n\n` +
          'Kontoen blir satt som uverifisert og må verifisere den nye ' +
          'adressen på nytt.'
      )
    ) {
      return
    }
    try {
      await changeEmail(found.uid, target)
      setNotice(`E-post endret til ${target}.`)
      // Re-fetch so the card reflects the new (unverified) address.
      setFound(await lookupUser(target))
      setQuery(target)
      setNewEmail('')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const isPasswordUser = found?.providers.includes('password') ?? false

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Brukere</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Slå opp en konto og overstyr e-postadressen — f.eks. for en bruker som
          registrerte feil adresse og dermed sitter fast som uverifisert.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Finn bruker</CardTitle>
          <CardDescription>
            Søk på den nåværende (feilstavede) e-postadressen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              void onSearch()
            }}
          >
            <Input
              type="email"
              value={query}
              placeholder="bruker@eksempel.no"
              onChange={(e) => setQuery(e.target.value)}
              className="max-w-sm"
            />
            <Button type="submit" disabled={loading || !query.trim()}>
              {loading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Search className="size-4" />
              )}
              Søk
            </Button>
          </form>
        </CardContent>
      </Card>

      {error && (
        <div className="border-destructive/40 bg-destructive/10 text-destructive rounded-md border px-4 py-3 text-sm">
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded-md border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800">
          {notice}
        </div>
      )}

      {found && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {found.displayName || found.email || found.uid}
            </CardTitle>
            <CardDescription className="flex flex-wrap items-center gap-2">
              <span className="font-mono">{found.email ?? '—'}</span>
              {found.emailVerified ? (
                <Badge variant="secondary">Verifisert</Badge>
              ) : (
                <Badge variant="destructive">Uverifisert</Badge>
              )}
              {found.disabled && <Badge variant="destructive">Deaktivert</Badge>}
              {found.providers.map((p) => (
                <Badge key={p} variant="outline">
                  {p}
                </Badge>
              ))}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="text-muted-foreground grid grid-cols-[8rem_1fr] gap-y-1 text-xs">
              <dt>UID</dt>
              <dd className="font-mono">{found.uid}</dd>
              <dt>Opprettet</dt>
              <dd>
                {found.creationTime
                  ? new Date(found.creationTime).toLocaleString()
                  : '—'}
              </dd>
            </dl>

            {!isPasswordUser && (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Dette er en føderert konto (f.eks. Google) uten passord.
                E-postadressen styres av identitetsleverandøren og bør normalt
                ikke overstyres her.
              </p>
            )}

            <div>
              <label className="text-sm font-medium">Ny e-postadresse</label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  type="email"
                  value={newEmail}
                  placeholder="riktig@eksempel.no"
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="max-w-sm"
                />
                <Button
                  variant="destructive"
                  onClick={() => void onChangeEmail()}
                  disabled={loading || !newEmail.trim()}
                >
                  {loading ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <UserPen className="size-4" />
                  )}
                  Endre e-post
                </Button>
              </div>
              <p className="text-muted-foreground mt-1 text-xs">
                Adressen settes som uverifisert; brukeren må verifisere den nye
                adressen på nytt.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
